import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Menu,
  X,
  Sun,
  Moon,
  Download,
  ArrowUp,
  Github,
  Linkedin,
  Mail,
} from 'lucide-react'
import { useTheme } from '../contexts/ThemeContext'
import { useCVModal } from '../contexts/CVModalContext'
import { PROFILE } from '../data/profile'
import {
  initGSAP,
  prefersReducedMotion,
  scrollToSection,
  scrollToTop,
} from '../lib/motion'

const NAV_ITEMS = [
  { id: 'about', label: 'About' },
  { id: 'experience', label: 'Experience' },
  { id: 'projects', label: 'Projects' },
  { id: 'contact', label: 'Contact' },
]

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false)
  const [activeId, setActiveId] = useState('')
  const [menuOpen, setMenuOpen] = useState(false)
  const { theme, toggleTheme } = useTheme()
  const { openModal } = useCVModal()
  const navigate = useNavigate()
  const location = useLocation()
  const onHome = location.pathname === '/'

  const barRef = useRef<HTMLElement>(null)
  const progressRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (prefersReducedMotion()) return
    const gsapApi = initGSAP()
    const ctx = gsapApi.context(() => {
      gsapApi.fromTo(
        barRef.current,
        { y: -32, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.7, ease: 'power3.out', delay: 0.2 }
      )
      gsapApi.fromTo(
        '[data-nav-item]',
        { y: -12, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.5,
          stagger: 0.06,
          ease: 'power2.out',
          delay: 0.35,
        }
      )
    }, barRef)
    return () => ctx.revert()
  }, [])

  useEffect(() => {
    if (prefersReducedMotion()) return
    const gsapApi = initGSAP()
    const progressTween = gsapApi.to(progressRef.current, {
      scaleX: 0,
      ease: 'none',
      scrollTrigger: {
        start: 0,
        end: 'max',
        onUpdate: (self) => {
          if (progressRef.current) {
            gsapApi.set(progressRef.current, { scaleX: self.progress })
          }
        },
      },
    })
    return () => {
      progressTween.scrollTrigger?.kill()
      progressTween.kill()
    }
  }, [])

  useEffect(() => {
    const sections = NAV_ITEMS.map((item) =>
      document.getElementById(item.id)
    ).filter((el): el is HTMLElement => el !== null)

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id)
        })
      },
      { rootMargin: '-40% 0px -55% 0px' }
    )

    sections.forEach((section) => observer.observe(section))
    return () => observer.disconnect()
  }, [location.pathname])

  useEffect(() => {
    const menu = menuRef.current
    if (!menu) return
    if (menuOpen) {
      if (prefersReducedMotion()) {
        menu.style.opacity = '1'
        menu.style.transform = 'none'
      } else {
        const gsapApi = initGSAP()
        gsapApi.fromTo(
          menu,
          { opacity: 0, y: -8 },
          { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' }
        )
      }
    }
  }, [menuOpen])

  const handleNav = (id: string) => {
    setMenuOpen(false)
    if (id === '') {
      if (onHome) scrollToTop(true)
      else navigate('/')
      return
    }
    if (onHome) {
      scrollToSection(`#${id}`)
    } else {
      navigate('/', { state: { scrollTo: id } })
    }
  }

  return (
    <nav
      ref={barRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/75 dark:bg-neutral-950/70 backdrop-blur-xl border-b border-neutral-200/70 dark:border-white/6'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="container-width flex items-center justify-between h-16">
        <button
          onClick={() => handleNav('')}
          className="flex items-center gap-3 group"
          aria-label="Back to top"
        >
          <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-neutral-900 dark:bg-neutral-800 border border-neutral-200 dark:border-white/8 text-primary-400 font-mono text-sm font-semibold">
            E.
          </span>
          <span className="hidden sm:flex flex-col text-left">
            <span className="text-sm font-semibold leading-none">
              Ezzddinne Mhamdi
            </span>
            <span className="mono-label mt-1.5">platform engineer</span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              data-nav-item
              onClick={() => handleNav(item.id)}
              className={`relative px-3.5 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                activeId === item.id
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white'
              }`}
            >
              {item.label}
              <span
                className={`absolute left-3 right-3 -bottom-0.5 h-px bg-primary-500 dark:bg-primary-400 transition-transform duration-300 origin-left ${
                  activeId === item.id ? 'scale-x-100' : 'scale-x-0'
                }`}
              />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={openModal}
            data-nav-item
            className="hidden lg:inline-flex btn-secondary !py-2 !px-4 text-sm"
          >
            <Download size={15} />
            <span>CV</span>
          </button>

          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            data-nav-item
            className="flex items-center justify-center w-10 h-10 rounded-lg border border-neutral-200 dark:border-white/8 text-neutral-600 dark:text-neutral-300 hover:border-primary-400/50 hover:text-primary-600 dark:hover:text-primary-300 transition-colors duration-200"
          >
            {theme === 'light' ? <Moon size={17} /> : <Sun size={17} />}
          </button>

          <button
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="Toggle menu"
            data-nav-item
            aria-expanded={menuOpen}
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg border border-neutral-200 dark:border-white/8 text-neutral-600 dark:text-neutral-300"
          >
            {menuOpen ? <X size={19} /> : <Menu size={19} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          ref={menuRef}
          className="lg:hidden border-t border-neutral-200/70 dark:border-white/6 bg-white/90 dark:bg-neutral-950/90 backdrop-blur-xl"
        >
          <div className="container-width py-4 flex flex-col">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNav(item.id)}
                className={`flex items-center justify-between py-3.5 border-b border-neutral-100 dark:border-white/5 text-left ${
                  activeId === item.id
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-neutral-700 dark:text-neutral-200'
                }`}
              >
                <span className="text-lg font-medium">{item.label}</span>
                <ArrowUp className="rotate-90 w-4 h-4 opacity-40" />
              </button>
            ))}
            <button
              onClick={() => {
                openModal()
                setMenuOpen(false)
              }}
              className="mt-4 flex items-center justify-center gap-2 btn-primary w-full"
            >
              <Download size={16} />
              <span>Download CV</span>
            </button>

            <div className="mt-6 border-t border-neutral-200 dark:border-white/5 pt-5">
              <div className="flex gap-3">
                {[
                  { label: 'GitHub', href: PROFILE.github, Icon: Github },
                  { label: 'LinkedIn', href: PROFILE.linkedin, Icon: Linkedin },
                  {
                    label: 'Email',
                    href: `mailto:${PROFILE.email}`,
                    Icon: Mail,
                  },
                ].map(({ label, href, Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target={href.startsWith('http') ? '_blank' : undefined}
                    rel={
                      href.startsWith('http')
                        ? 'noopener noreferrer'
                        : undefined
                    }
                    aria-label={label}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center justify-center w-10 h-10 rounded-lg border border-neutral-200 dark:border-white/8 text-neutral-500 dark:text-neutral-400 hover:text-primary-500 dark:hover:text-primary-400 hover:border-primary-400/40 transition-colors duration-200"
                  >
                    <Icon size={17} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        ref={progressRef}
        className="absolute bottom-0 left-0 right-0 h-px bg-primary-500 dark:bg-primary-400 origin-left"
        style={{ transform: 'scaleX(0)' }}
        aria-hidden="true"
      />
    </nav>
  )
}

export default Navbar